import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';
import { db } from '@vibe/db/server';
import { agencyContacts } from '@generated/models';
import { executeAiRequest } from '@/api/ai-service';
import { AdAgenciesBoard } from '@api/BoardSDK';
import { withBoardAuth } from '@generated/server/boardAuth.server';
import { requireStaff } from '@generated/server/staff.server';
import { guessEmails } from '@generated/hooks/emailGuess';

export type FoundContact = { name: string; title: string; email: string; emailKind: 'published' | 'pattern guess' | 'none'; source: string };

async function searchAgency(id: string) {
    const data = { id };
    const a = await withBoardAuth(() => new AdAgenciesBoard().item(data.id).withColumns(['website', 'borougharea', 'focus', 'notes']).execute());
    if (!a) throw new Error('Agency not found');
    const site = a.website?.url ?? '';
    const prompt = `Find people at the advertising/media agency "${a.name}"${site ? ` (website ${site})` : ''}, New York office (${a.borougharea ?? 'NYC'}), who buy or plan media, especially print and local/community newspaper ads.
Wanted job titles: media buyer, media planner, media supervisor, media director, print buyer, account supervisor, account director, director of local/community media, owner/principal (for small firms).
Also find the company's email address pattern (check RocketReach, Hunter.io, ZoomInfo, Lead411, ContactOut and the agency's own site/press pages; write it like [first][last]@domain or [first].[last]@domain) and a general contact email (info@, hello@, newbusiness@) if published, plus the New York office mailing address. When a person's email isn't published but the pattern is known, build it from the pattern and mark it "pattern guess".
Rules: only list real people you found in sources (agency website team pages, press releases, news articles, LinkedIn mentions, conference speaker lists). Never invent a person. For each person: if their email was published somewhere, emailKind "published"; if you built it from the company's known pattern, emailKind "pattern guess"; otherwise email "" and emailKind "none". Give the source URL where you found the person. Up to 8 people, most relevant to buying print ads first.`;
    const ask = (maxResults: number) => executeAiRequest(prompt, {
      systemPrompt: 'You are a careful sales researcher for two NYC community newspapers. Accuracy matters more than quantity; an empty list is better than invented people.',
      schema: {
        type: 'object', additionalProperties: false, required: ['contacts', 'emailPattern', 'generalEmail', 'summary', 'mailingAddress'],
        properties: {
          contacts: { type: 'array', maxItems: 8, items: { type: 'object', additionalProperties: false, required: ['name', 'title', 'email', 'emailKind', 'source'], properties: {
            name: { type: 'string' }, title: { type: 'string' }, email: { type: 'string' },
            emailKind: { type: 'string', enum: ['published', 'pattern guess', 'none'] }, source: { type: 'string', description: 'URL where this person was found' },
          } } },
          emailPattern: { type: 'string', description: 'e.g. first.last@agency.com, or "" if unknown' },
          generalEmail: { type: 'string', description: 'Published general/new-business email, or ""' },
          summary: { type: 'string', description: 'One or two sentences on who to approach first and why' },
          mailingAddress: { type: 'string', description: 'The New York office street address as "Street, City, State ZIP", or "" if not found' },
        },
      },
      webSearch: { useWebSearch: true, maxResults },
    });
    // The AI service sometimes has a momentary hiccup (Internal Server Error) — wait and retry, with a lighter search on later tries.
    let res: Awaited<ReturnType<typeof ask>> | undefined;
    for (let attempt = 0; attempt < 3 && !res; attempt++) {
      try { res = await ask(attempt === 0 ? 10 : 6); }
      catch (e) {
        console.error('contact search attempt', attempt, e);
        if ((e as { code?: string })?.code === 'AI_CREDITS_EXHAUSTED') throw new Error('CREDITS: Your monday AI credits have run out for now, so the search cannot run.');
        if (attempt < 2) await new Promise((r) => setTimeout(r, 2500 * (attempt + 1)));
      }
    }
    if (!res) throw new Error('The search service is busy right now. Please try again in a minute.');
    const out = res.data as { contacts: FoundContact[]; emailPattern: string; generalEmail: string; summary: string; mailingAddress: string } | undefined;
    return { agency: a, contacts: out?.contacts ?? [], emailPattern: out?.emailPattern ?? '', generalEmail: out?.generalEmail ?? '', summary: out?.summary ?? '', mailingAddress: out?.mailingAddress ?? '' };
}

export const findAgencyContacts = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string(), id: z.string().regex(/^\d+$/) }))
  .handler(async ({ data }) => {
    await requireStaff(data.pass);
    const { agency: _a, ...rest } = await searchAgency(data.id);
    return rest;
  });

/** Agencies with no mailing address yet (ids + names), for the one-click bulk search. */
export const agenciesWithoutAddress = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string() }))
  .handler(async ({ data }) => {
    await requireStaff(data.pass);
    return withBoardAuth(async () => {
      const b = new AdAgenciesBoard();
      const out: { id: string; name: string }[] = [];
      let cursor: string | null | undefined;
      do {
        const r = cursor ? await b.items().withColumns(['address']).withPagination({ limit: 100, cursor }).execute() : await b.items().withColumns(['address']).withPagination({ limit: 100 }).execute();
        for (const i of r.items) if (!i.address?.trim()) out.push({ id: i.id, name: i.name });
        cursor = r.cursor;
      } while (cursor);
      return out;
    });
  });

/** Searches one agency and adds everyone with an email (found or built from the pattern, plus the general inbox) to its email list; fills in the mailing address if found. */
export const autoAddContacts = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string(), id: z.string().regex(/^\d+$/) }))
  .handler(async ({ data }) => {
    await requireStaff(data.pass);
    const r = await searchAgency(data.id);
    const main = (await withBoardAuth(() => new AdAgenciesBoard().item(data.id).withColumns(['email', 'address']).execute()));
    const existing = (await db.collection(agencyContacts).where('agencyId', '==', data.id).limit(100).get()).map((c) => c.data.email.toLowerCase());
    if (main?.email?.email) existing.push(main.email.email.toLowerCase());
    const people = [...r.contacts];
    if (r.generalEmail.includes('@')) people.push({ name: '', title: 'General inbox', email: r.generalEmail, emailKind: 'published', source: '' });
    let added = 0;
    for (const p of people) {
      let email = p.email.trim().toLowerCase();
      let kind: string = p.emailKind;
      if (!email.includes('@') && p.name) { email = guessEmails(p.name, r.emailPattern, r.generalEmail.split('@')[1] ?? '')[0] ?? ''; kind = 'pattern guess'; }
      if (!email.includes('@') || existing.includes(email)) continue;
      existing.push(email);
      await db.collection(agencyContacts).add({ agencyId: data.id, name: p.name, title: p.title, email, emailKind: kind, createdAt: new Date().toISOString() });
      added++;
    }
    const addr = r.mailingAddress.trim();
    if (addr && !main?.address?.trim()) await withBoardAuth(() => new AdAgenciesBoard().item(data.id).update({ address: addr }).execute());
    return { added, address: addr };
  });

/** Saves a found contact: as the main contact (name + email), or appended to the agency's notes. */
export const saveFoundContact = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string(), id: z.string().regex(/^\d+$/), mode: z.enum(['main', 'notes', 'list']), name: z.string().max(200), title: z.string().max(200), email: z.string().max(200), emailKind: z.string().max(40) }))
  .handler(async ({ data }) => {
    await requireStaff(data.pass);
    return withBoardAuth(async () => {
      const b = new AdAgenciesBoard();
      const cur = await b.item(data.id).withColumns(['notes']).execute();
      const line = `${data.name}${data.title ? ` (${data.title})` : ''}${data.email ? ` - ${data.email}${data.emailKind === 'pattern guess' ? ' [guessed, verify]' : ''}` : ''}`;
      if (data.mode === 'list') {
        const email = data.email.trim().toLowerCase();
        if (!email.includes('@')) throw new Error('No email for this person');
        const existing = await db.collection(agencyContacts).where('agencyId', '==', data.id).limit(100).get();
        if (!existing.some((c) => c.data.email.toLowerCase() === email)) await db.collection(agencyContacts).add({ agencyId: data.id, name: data.name, title: data.title, email, emailKind: data.emailKind, createdAt: new Date().toISOString() });
        return { ok: true };
      }
      if (data.mode === 'main') {
        const email = data.email.includes('@') ? { email: data.email.trim(), label: data.email.trim() } : undefined;
        await b.item(data.id).update({ contactName: data.name, ...(email ? { email } : {}) }).execute();
      } else {
        await b.item(data.id).update({ notes: [cur?.notes, `Other contact: ${line}`].filter(Boolean).join('\n') }).execute();
      }
      return { ok: true };
    });
  });

export const removeAgencyContact = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string(), contactId: z.string().min(1) }))
  .handler(async ({ data }) => {
    await requireStaff(data.pass);
    await db.collection(agencyContacts).doc(data.contactId).delete();
    return { ok: true };
  });

export const updateAgencyContact = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string(), contactId: z.string().min(1), name: z.string().max(200), title: z.string().max(200), email: z.string().max(200) }))
  .handler(async ({ data }) => {
    await requireStaff(data.pass);
    const email = data.email.trim().toLowerCase();
    if (!email.includes('@')) throw new Error('Please enter a valid email');
    await db.collection(agencyContacts).doc(data.contactId).update({ name: data.name.trim(), title: data.title.trim(), email, emailKind: 'published' });
    return { ok: true };
  });
